import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import 'react-toastify/dist/ReactToastify.css'

import * as Sentry from '@sentry/react'

import App from '@src/App'
import '@src/index.css'
import setupBigIntJSON from '@src/jsonBigInt'
import reportWebVitals from '@src/reportWebVitals'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('index')

// Block Phantom from injecting its provider
;(function blockPhantom() {
    type IPhantomEnabledWindow = Window & {
        phantom?: { ethereum?: any }
        ethereum?: any
    }

    const win = window as IPhantomEnabledWindow

    if (win.phantom?.ethereum) {
        logger.warn('&&& Phantom is attempting to override MetaMask!')

        // If Phantom has already replaced window.ethereum, restore the correct provider
        if (win.ethereum === win.phantom.ethereum) {
            logger.info('&&& Resetting window.ethereum to prevent Phantom hijack...')
            if (win.ethereum.providers && Array.isArray(win.ethereum.providers)) {
                const metamaskProvider = win.ethereum.providers.find((p: any) => p.isMetaMask)
                win.ethereum = metamaskProvider || win.ethereum
            }
        }

        // Try to neutralize Phantom's ethereum in a more defensive way
        try {
            // First check if we can modify the property
            const phantomDesc = Object.getOwnPropertyDescriptor(win.phantom, 'ethereum')
            if (phantomDesc && phantomDesc.configurable) {
                Object.defineProperty(win.phantom, 'ethereum', {
                    value: null,
                    writable: false,
                    configurable: false,
                })
            } else {
                // If we can't redefine it, try to at least set it to null if writable
                if (phantomDesc && phantomDesc.writable) {
                    win.phantom.ethereum = null
                }
                logger.warn(
                    '&&& Could not fully block Phantom (property not configurable), but attempted to neutralize it'
                )
            }
        } catch (error) {
            logger.error('&&& Failed to override Phantom.ethereum:', error)
        }
    }
})()

Sentry.init({
    dsn: 'https://51d13d4e6600f759933e1ea3d9d670c7@o4508241426513920.ingest.us.sentry.io/4508241436213248',
    integrations: [],
    // Set sampling rate to reduce the number of events sent to Sentry
    tracesSampleRate: 0.2, // Sample 20% of transactions
    // Optional: Add different sampling rates for specific operations
    tracesSampler: samplingContext => {
        // Adjust sampling based on the operation or other context
        const operation = samplingContext.transactionContext?.op

        // Sample exchange operations at a higher rate
        if (operation?.includes('exchange')) {
            return 0.5 // 50% sampling for exchange operations
        }

        // Lower sampling rate for less critical operations
        if (operation?.includes('ui.render')) {
            return 0.1 // 10% sampling for UI renders
        }

        // Default to the global sample rate
        return 0.2
    },
})

/**
 * Overrides JSON.parse and JSON.stringify to handle BigInt values, which JSON does not support by default.
 */
setupBigIntJSON()

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
root.render(
    <BrowserRouter>
        <App />
    </BrowserRouter>
)

reportWebVitals()
